import React from "react"

export interface EventTicketSummary {
  id: number
  title: string
  ticket_type?: string
  ticket_price?: number
  max_tickets?: number
  nada_url: string
  tickets_sold?: number
  tickets_remaining?: number
  current_estimated_payout?: number
  current_estimated_fee?: number
}

export interface EventInformationPost {
  title?: string
  nada_url?: string
  start_dt?: string
  end_dt?: string
  ticket_price?: number
  location?:
    | {
        displayName?: string
      }
    | string
  max_tickets?: number
  tickets_sold?: number
  tickets_remaining?: number
  current_estimated_payout?: number
  current_estimated_fee?: number
  event_ticket_summary?: EventTicketSummary
  content_hash?: Record<string, unknown>
}

interface EventInformationProps {
  post: EventInformationPost
  cashTicketsTotal?: number | null
  cashTicketsPurchased?: number | null
}

const formatDateTime = (dateString?: string | null): string | null => {
  if (!dateString) return null
  return new Date(dateString).toLocaleString()
}

export const EventInformation: React.FC<EventInformationProps> = ({
  post,
  cashTicketsTotal,
  cashTicketsPurchased,
}) => {
  const summary = post.event_ticket_summary
  const ticketPrice = summary?.ticket_price ?? post.ticket_price
  const maxTickets = summary?.max_tickets ?? post.max_tickets
  const ticketsSold = summary?.tickets_sold ?? post.tickets_sold
  const ticketsRemaining = summary?.tickets_remaining ?? post.tickets_remaining
  const currentEstimatedPayout =
    summary?.current_estimated_payout ?? post.current_estimated_payout
  const currentEstimatedFee =
    summary?.current_estimated_fee ?? post.current_estimated_fee
  const totalRevenueWithCash =
    currentEstimatedPayout !== undefined && cashTicketsTotal
      ? currentEstimatedPayout + cashTicketsTotal
      : undefined

  return (
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6">
      <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">
        Event Information
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 text-sm">
        {post.start_dt && (
          <div>
            <span className="text-gray-600">Start Time:</span>
            <div className="font-medium text-black-800">
              {formatDateTime(post.start_dt)}
            </div>
          </div>
        )}
        {post.end_dt && (
          <div>
            <span className="text-gray-600">End Time:</span>
            <div className="font-medium text-black-800">
              {formatDateTime(post.end_dt)}
            </div>
          </div>
        )}
        {ticketPrice !== undefined && (
          <div>
            <span className="text-gray-600">Price:</span>
            <div className="font-medium text-black-800">${ticketPrice}</div>
          </div>
        )}
        {post.location && (
          <div>
            <span className="text-gray-600">Location:</span>
            <div className="font-medium text-black-800">
              {typeof post.location === "string"
                ? post.location
                : post.location.displayName || ""}
            </div>
          </div>
        )}
        {maxTickets !== undefined && (
          <div>
            <span className="text-gray-600">Max Tickets:</span>
            <div className="font-medium text-black-800">{maxTickets}</div>
          </div>
        )}
        {ticketsSold !== undefined && (
          <div>
            <span className="text-gray-600">Tickets Sold:</span>
            <div className="font-medium text-black-800">{ticketsSold}</div>
          </div>
        )}
        {ticketsRemaining !== undefined && (
          <div>
            <span className="text-gray-600">Tickets Remaining (official):</span>
            <div className="font-medium text-black-800">{ticketsRemaining}</div>
          </div>
        )}
        {ticketsRemaining !== undefined && cashTicketsPurchased ? (
          <div>
            <span className="text-gray-600">Tickets Remaining (sub cash):</span>
            <div className="font-medium text-black-800">
              {ticketsRemaining - cashTicketsPurchased}
            </div>
          </div>
        ) : null}
        {currentEstimatedPayout !== undefined && (
          <div>
            <span className="text-gray-600">Current Est. Payout:</span>
            <div className="font-medium text-black-800">
              ${currentEstimatedPayout}
            </div>
          </div>
        )}
        {currentEstimatedFee !== undefined && currentEstimatedFee > 0 && (
          <div>
            <span className="text-gray-600">bandNada Fee:</span>
            <div className="font-medium text-red-600">
              ${currentEstimatedFee.toFixed(2)}
            </div>
            <div className="text-xs text-gray-400">(already deducted from est. payout)</div>
          </div>
        )}
        {totalRevenueWithCash !== undefined && (
          <div>
            <span className="text-gray-600">Total Revenue w/ Cash:</span>
            <div className="font-medium text-black-800">
              ${Math.round(totalRevenueWithCash)}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
